import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useClient, useCreateClient, useUpdateClient } from '../../hooks/useClients';
import type { ClientStatus } from '../../types';

const clientSchema = z.object({
  name: z.string().min(1, 'Client name is required').max(255, 'Name must be 255 characters or less'),
  industry: z.string().max(255, 'Industry must be 255 characters or less').optional().or(z.literal('')),
  tax_id: z.string().max(50, 'Tax ID must be 50 characters or less').optional().or(z.literal('')),
  address: z.string().max(500, 'Address must be 500 characters or less').optional().or(z.literal('')),
  notes: z.string().max(2000, 'Notes must be 2000 characters or less').optional().or(z.literal('')),
  status: z.enum(['active', 'inactive', 'churned', 'archived'] as const).default('active'),
});

type ClientFormData = z.infer<typeof clientSchema>;

export default function ClientForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: existingClient, isLoading: isLoadingClient } = useClient(id ?? '');
  const createClient = useCreateClient();
  const updateClient = useUpdateClient(id ?? '');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      industry: '',
      tax_id: '',
      address: '',
      notes: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (existingClient && isEditing) {
      reset({
        name: existingClient.name,
        industry: existingClient.industry || '',
        tax_id: '',
        address: '',
        notes: existingClient.notes || '',
        status: existingClient.status,
      });
    }
  }, [existingClient, isEditing, reset]);

  const onSubmit = async (data: ClientFormData) => {
    try {
      const payload = {
        name: data.name,
        industry: data.industry || undefined,
        notes: data.notes || undefined,
        status: data.status as ClientStatus,
      };

      if (isEditing) {
        await updateClient.mutateAsync(payload);
        navigate(`/clients/${id}`);
      } else {
        const newClient = await createClient.mutateAsync(payload);
        navigate(`/clients/${newClient.id}`);
      }
    } catch {
      // Error handled by React Query
    }
  };

  if (isEditing && isLoadingClient) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={isEditing ? `/clients/${id}` : '/clients'}
          className="rounded-md p-1 text-gray-400 hover:text-gray-600"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Client' : 'New Client'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing
              ? 'Update client information below.'
              : 'Fill in the details to create a new client.'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                {...register('name')}
                className={`mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                  errors.name ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                placeholder="e.g., Acme Corporation"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                Industry
              </label>
              <input
                type="text"
                id="industry"
                {...register('industry')}
                className={`mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                  errors.industry ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                placeholder="e.g., Technology, Healthcare, Finance"
              />
              {errors.industry && (
                <p className="mt-1 text-sm text-red-600">{errors.industry.message}</p>
              )}
            </div>

            {/* Tax ID */}
            <div>
              <label htmlFor="tax_id" className="block text-sm font-medium text-gray-700">
                Tax ID
              </label>
              <input
                type="text"
                id="tax_id"
                {...register('tax_id')}
                className={`mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                  errors.tax_id ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                placeholder="e.g., 123-456-789"
              />
              {errors.tax_id && (
                <p className="mt-1 text-sm text-red-600">{errors.tax_id.message}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Address
              </label>
              <textarea
                id="address"
                rows={3}
                {...register('address')}
                className={`mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                  errors.address ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                placeholder="Client address"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                {...register('status')}
                className="mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="churned">Churned</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="notes"
                rows={4}
                {...register('notes')}
                className={`mt-1 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                  errors.notes ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                placeholder="Additional notes about this client..."
              />
              {errors.notes && (
                <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link
            to={isEditing ? `/clients/${id}` : '/clients'}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || createClient.isPending || updateClient.isPending}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting || createClient.isPending || updateClient.isPending
              ? 'Saving...'
              : isEditing
              ? 'Update Client'
              : 'Create Client'}
          </button>
        </div>

        {/* Error Display */}
        {(createClient.isError || updateClient.isError) && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">
              {isEditing
                ? 'Failed to update client. Please try again.'
                : 'Failed to create client. Please try again.'}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
